import React from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, Button, Input, Box } from '@chakra-ui/react';
import AuthHeader from '@/components/auth-header';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import api from '@/utils/api';

const formSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof formSchema>;

const InviteNewUser: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.put('invite/confirm-set-password', {
        token,
        password: data.password,
        c_password: data.confirmPassword,
      });

      toaster.create({
        type: 'success',
        title: 'Password reset successfully.',
      });

      navigate('/auth/login');
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to reset password. Please try again.',
      });
    }
  };

  return (
    <Stack gap={6}>
      <AuthHeader title='Set your password' />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Field label='Password' invalid={!!errors.password} errorText={errors.password?.message}>
            <Input {...register('password')} type={'password'} />
          </Field>
          <Field
            label='Confirm password'
            invalid={!!errors.confirmPassword}
            errorText={errors.confirmPassword?.message}
          >
            <Input {...register('confirmPassword')} type={'password'} />
          </Field>
          <Button type={'submit'} width={'full'} loading={isSubmitting} loadingText='Loading...'>
            Submit
          </Button>
        </Stack>
      </form>
      <Box textAlign={'center'}>
        <Button fontSize={'sm'} variant={'ghost'} asChild>
          <NavLink to='/auth/login'>Never mind, I remember my password</NavLink>
        </Button>
      </Box>
    </Stack>
  );
};

export default InviteNewUser;
